/**
 * Telemetry Events — Type Contracts
 * ===================================
 * Defines all event names, parameter shapes, and consent classification
 * for the Unified Telemetry Layer.
 *
 * CONSENT CLASSIFICATION:
 *   - BEHAVIORAL: Gated on user consent. userId omitted when anonymous.
 *   - OPERATIONAL: Consent-exempt. Fires regardless of consent state.
 */

// ---------------------------------------------------------------------------
// Event Names
// ---------------------------------------------------------------------------

export const TelemetryEventName = {
  // Lifecycle — BEHAVIORAL
  SESSION_START: 'session_start',
  SCREEN_VIEW: 'screen_view',

  // Generic flow bookmarks — BEHAVIORAL
  FLOW_START: 'flow_start',
  FLOW_COMPLETE: 'flow_complete',
  FLOW_ERROR: 'flow_error',

  // Recipe wizard — BEHAVIORAL
  RECIPE_STEP_SAVED: 'recipe_step_saved',
  RECIPE_DRAFT_SAVED: 'recipe_draft_saved',

  // Shopping list wizard — BEHAVIORAL
  SHOPPING_LIST_MODE_SELECTED: 'shopping_list_mode_selected',
  SHOPPING_LIST_ITEM_ADDED: 'shopping_list_item_added',
  SHOPPING_LIST_STEP_COMPLETE: 'shopping_list_step_complete',

  // Shopping list view — BEHAVIORAL
  SHOPPING_LIST_ITEM_PURCHASED: 'shopping_list_item_purchased',
  SHOPPING_LIST_DELETED: 'shopping_list_deleted',

  // Recipe CRUD — BEHAVIORAL
  RECIPE_DELETED: 'recipe_deleted',

  // Cookbook CRUD — BEHAVIORAL
  COOKBOOK_CREATED: 'cookbook_created',
  COOKBOOK_UPDATED: 'cookbook_updated',
  COOKBOOK_DELETED: 'cookbook_deleted',

  // Planner — BEHAVIORAL
  PLANNER_ENTRY_ADDED: 'planner_entry_added',
  PLANNER_ENTRY_REMOVED: 'planner_entry_removed',

  // Operational cross-link — OPERATIONAL (consent-exempt)
  CRITICAL_ERROR: 'critical_error',
} as const;

export type TelemetryEventNameValue =
  (typeof TelemetryEventName)[keyof typeof TelemetryEventName];

// ---------------------------------------------------------------------------
// Defined Flow Names
// ---------------------------------------------------------------------------

export const FlowName = {
  RECIPE_CREATION: 'recipe_creation',
  SHOPPING_LIST_GENERATION: 'shopping_list_generation',
  SHOPPING_LIST_MANUAL: 'shopping_list_manual',
  WEEKLY_PLANNER_SETUP: 'weekly_planner_setup',
  COOKBOOK_CREATION: 'cookbook_creation',
} as const;

export type FlowNameValue = (typeof FlowName)[keyof typeof FlowName] | string;

// ---------------------------------------------------------------------------
// Event Parameter Shapes
// ---------------------------------------------------------------------------

export interface ScreenViewParams {
  screen_name: string;
}

export interface FlowStartParams {
  flow: FlowNameValue;
  step?: string;
}

export interface FlowCompleteParams {
  flow: FlowNameValue;
  duration_ms: number;
}

export interface FlowErrorParams {
  flow: FlowNameValue;
  error_type: string;
  step?: string;
}

export interface CriticalErrorParams {
  sentry_event_id: string;
  error_type: string;
}

// ---------------------------------------------------------------------------
// Recipe Wizard Params
// ---------------------------------------------------------------------------

export type RecipeWizardStepName =
  | 'general_data'
  | 'ingredients'
  | 'preparation'
  | 'notes';

export interface RecipeStepSavedParams {
  recipe_id: string;
  recipe_title: string;
  step_number: 1 | 2 | 3 | 4;
  step_name: RecipeWizardStepName;
  is_edit: boolean;
}

export interface RecipeDraftSavedParams {
  recipe_id: string;
  recipe_title: string;
  step_number: 1 | 2 | 3 | 4;
  step_name: RecipeWizardStepName;
}

export interface RecipeFlowCompleteParams extends FlowCompleteParams {
  recipe_id: string;
  recipe_title: string;
  is_edit: boolean;
}

// ---------------------------------------------------------------------------
// Shopping List Wizard Params
// ---------------------------------------------------------------------------

export type ShoppingListMode = 'manual' | 'generated';

export interface ShoppingListModeSelectedParams {
  mode: ShoppingListMode;
}

export interface ShoppingListItemAddedParams {
  total_items: number;
}

export type ShoppingListWizardStep = 'gen_configure' | 'extras';

export interface ShoppingListStepCompleteParams {
  step: ShoppingListWizardStep;
  people_count?: number;
  extra_count?: number;
}

export interface ShoppingListManualFlowCompleteParams extends FlowCompleteParams {
  item_count: number;
}

export interface ShoppingListGeneratedFlowCompleteParams extends FlowCompleteParams {
  item_count: number;
  people_count: number;
  planner_items_count: number;
  planner_items_excluded: number;
  extra_count: number;
}

// ---------------------------------------------------------------------------
// Shopping List View Params
// ---------------------------------------------------------------------------

export interface ShoppingListItemPurchasedParams {
  items_remaining: number;
  total_items: number;
}

// ---------------------------------------------------------------------------
// Recipe CRUD Params
// ---------------------------------------------------------------------------

export interface RecipeDeletedParams {
  recipe_id: string;
  recipe_title: string;
  was_draft: boolean;
}

// ---------------------------------------------------------------------------
// Cookbook CRUD Params
// ---------------------------------------------------------------------------

export interface CookbookCreatedParams {
  cookbook_id: string;
  cookbook_title: string;
  recipe_count: number;
}

export interface CookbookUpdatedParams {
  cookbook_id: string;
  cookbook_title: string;
  recipe_count: number;
}

export interface CookbookDeletedParams {
  cookbook_id: string;
  cookbook_title: string;
}

// ---------------------------------------------------------------------------
// Planner Params
// ---------------------------------------------------------------------------

export interface PlannerEntryAddedParams {
  meal_slot: string;
  day_of_week: string;
}

export interface PlannerEntryRemovedParams {
  meal_slot: string;
}

// ---------------------------------------------------------------------------
// Unified TelemetryEvent (analytics side)
// ---------------------------------------------------------------------------

export interface TelemetryEvent {
  name: TelemetryEventNameValue;
  userId?: string;
  sessionId: string;
  environment: 'dev' | 'staging' | 'prod';
  timestamp: number;
  params?:
    | ScreenViewParams
    | FlowStartParams
    | FlowCompleteParams
    | RecipeFlowCompleteParams
    | ShoppingListManualFlowCompleteParams
    | ShoppingListGeneratedFlowCompleteParams
    | FlowErrorParams
    | RecipeStepSavedParams
    | RecipeDraftSavedParams
    | RecipeDeletedParams
    | ShoppingListModeSelectedParams
    | ShoppingListItemAddedParams
    | ShoppingListStepCompleteParams
    | ShoppingListItemPurchasedParams
    | CookbookCreatedParams
    | CookbookUpdatedParams
    | CookbookDeletedParams
    | PlannerEntryAddedParams
    | PlannerEntryRemovedParams
    | CriticalErrorParams;
}

// ---------------------------------------------------------------------------
// Buffered Event (offline buffer)
// ---------------------------------------------------------------------------

export interface BufferedEvent {
  id: string;
  target: 'sentry' | 'analytics';
  payload: TelemetryEvent;
  createdAt: number;
  retryCount: number;
}

export const EVENT_BUFFER_CAPACITY = 50 as const;
export const EVENT_BUFFER_STORAGE_KEY = 'telemetry_buffer' as const;

// ---------------------------------------------------------------------------
// PII Scrubbing — Sensitive Field Blocklist
// ---------------------------------------------------------------------------

export const PII_SENSITIVE_FIELDS = [
  'password',
  'token',
  'authorization',
  'cookie',
  'secret',
  'card',
  'cvv',
  'ssn',
  'credit',
] as const;

export type PiiSensitiveField = (typeof PII_SENSITIVE_FIELDS)[number];
